import * as React from "react"
import * as AvatarPrimitive from "@radix-ui/react-avatar"
import { cn } from "@/lib/utils"
import { imageUrl, imageSrcSet } from "@/lib/images"
import { pickPersonName } from "@/lib/personName"

interface AvatarProps extends React.ComponentPropsWithoutRef<typeof AvatarPrimitive.Root> {
  src?: string | null
  alt?: string
  fallback?: string | null
  size?: 'sm' | 'md' | 'lg'
}

const sizeClasses = {
  sm: "h-8 w-8",
  md: "h-10 w-10",
  lg: "h-12 w-12"
}

/**
 * The pixel size behind each class, so imgix is asked for what is drawn.
 *
 * `className` can override the size — the call detail asks for `h-24 w-24`. A
 * request smaller than the box would be visibly soft, so the fallback is the
 * largest of the three rather than the default: too many pixels only costs
 * bytes, too few costs a blurry face.
 */
const sizePixels = { sm: 32, md: 40, lg: 48 } as const
const OVERRIDDEN_SIZE = 96

export function Avatar({ src, alt, fallback, size = 'md', className, ...props }: AvatarProps) {
  /* U7: a person with no name (the API's `null`, or the old "Unknown") gets "?", never the "N" of "null". */
  const initials = pickPersonName(fallback)?.charAt(0).toUpperCase() || '?'

  /* A `className` carrying its own `h-/w-` means the size prop is not the truth
     any more. Rather than parse Tailwind, ask for the biggest avatar the app
     draws — it is still a hundredth of the original file. */
  const px = /\b[hw]-\d/.test(className || '') ? OVERRIDDEN_SIZE : sizePixels[size]
  const opts = { w: px, h: px, fit: 'crop' as const, crop: 'faces' }
  
  return (
    <AvatarPrimitive.Root
      className={cn(
        "relative flex shrink-0 overflow-hidden rounded-full",
        sizeClasses[size],
        className
      )}
      {...props}
    >
      <AvatarPrimitive.Image
        src={imageUrl(src, opts) || undefined}
        srcSet={imageSrcSet(src, opts)}
        alt={alt}
        loading="lazy"
        className="h-full w-full object-cover"
      />
      <AvatarPrimitive.Fallback
        className="flex h-full w-full items-center justify-center rounded-full bg-primary-50 text-primary"
      >
        {initials}
      </AvatarPrimitive.Fallback>
    </AvatarPrimitive.Root>
  )
}